import { Segmented, Tooltip } from 'antd';
import Button from 'components/common/Button';
import LinkButton from 'components/common/LinkButton/LinkButton';
import { NUTRITION_ADD_REMOVE_PRODUCTS_ENABLED } from 'constants/appConstants';
import {
  NUTRITION_FARM_TOTAL_HELP,
  NUTRITION_MISSING_REMOVAL_MESSAGE,
  NUTRITION_SEASON_NO_PLAN_MESSAGE,
  NUTRITION_NO_PLANS_MESSAGE,
} from 'constants/messageConstants';
import { PADDOCK_KEY_PREFIX } from 'constants/routeConstants';
import {
  INPUT_DOC_LENS_NUTRITION,
  INPUT_DOC_TAB_PLANNING,
  NUTRITION_COLLAPSE_ALL_LABEL,
  NUTRITION_COLLAPSE_CROP_LABEL,
  NUTRITION_COLLAPSE_PRODUCTS_LABEL,
  NUTRITION_AREA_UNIT_HA,
  NUTRITION_APPLIED_REMOVED_TOOLTIP,
  NUTRITION_AREA_COLUMN_HELP,
  NUTRITION_AREA_VARIES_NOTE,
  NUTRITION_AREA_VARIES_TITLE,
  NUTRITION_COLUMN_AREA,
  NUTRITION_COLUMN_PADDOCK,
  NUTRITION_COLUMN_PRODUCTS,
  NUTRITION_COLUMN_TOTAL,
  NUTRITION_EXPAND_ALL_LABEL,
  NUTRITION_EXPAND_CROP_LABEL,
  NUTRITION_EXPAND_PRODUCTS_LABEL,
  NUTRITION_PLAN_COUNT_LABEL,
  NUTRITION_PLAN_YEAR_FARM_LABEL,
  NUTRITION_FARM_VIEW_SEASONS,
  NUTRITION_GROUP_BY_CROP,
  NUTRITION_GROUP_BY_FARM,
  NUTRITION_GROUP_BY_LABEL,
  NUTRITION_GROUP_BY_LABELS,
  NUTRITION_PRODUCT_COUNT_LABEL,
  NUTRITION_ROW_FARM_TOTAL,
  NUTRITION_ROTATION_TITLE,
  NUTRITION_ROW_CROP_REMOVAL,
  NUTRITION_ROW_FARM_TOTAL_FOR,
  NUTRITION_SEASON_NO_PRODUCTS_LABEL,
  NUTRITION_PADDOCK_YEAR_LABEL,
  NUTRITION_UNIT_KG_PER_HA,
  NUTRITION_YEAR_ITEM,
} from 'constants/uiConstants';
import useFarmsLinkNavigate from 'pages/FarmsPage/hooks/useFarmsLinkNavigate';
import React, { useMemo, useState } from 'react';
import {
  LuAlertTriangle,
  LuChevronDown,
  LuChevronRight,
  LuChevronsDownUp,
  LuChevronsUpDown,
  LuInfo,
} from 'react-icons/lu';
import type {
  NutritionFarmView,
  NutritionGroupBy,
} from '../../../../../redux/slices/InsightsSlice';
import {
  NUTRIENT_DISPLAY_ORDER,
  NUTRIENT_SHORT_LABELS,
  type NutrientKey,
} from '../computeLiveTotals';
import type {
  AreaWeightedBalance,
  NutritionAudit,
  PaddockNutritionAudit,
} from './computeNutritionAudit';
import IconTip from './IconTip';
import TipContent, { type TipText } from './TipContent';
import NutrientBalanceValue from './NutrientBalanceValue';
import {
  formatAreaHa,
  formatKgPerHa,
  formatPlanSummary,
  formatSeasonCrops,
  formatYearCrops,
} from './nutritionFormat';
import ProductDate from './ProductDate';
import ProductName from './ProductName';
import ProductRate from './ProductRate';
import type { NutritionProgramEdit } from './useNutritionProgramEdit';
import AddProductControl from './AddProductControl';
import { groupPlansByCrop, type CropPlanRow } from './groupPlansByCrop';
import ProductRowMenu from './ProductRowMenu';

interface FarmNutritionTableProps {
  audit: NutritionAudit;
  view: NutritionFarmView;
  /** Columns in the balance view. */
  nutrients: NutrientKey[];
  /** The one nutrient the season-by-season view shows. */
  trendNutrient: NutrientKey;
  edit: NutritionProgramEdit;
  /** Which yield the year labels show, e.g. "Expected yield". */
  yieldLabel: string;
  /** Area and yield beside each year's crop (season view). */
  showCropDetails: boolean;
  /** Paddocks grouped by farm, or plans grouped by crop. */
  groupBy: NutritionGroupBy;
  onGroupByChange: (groupBy: NutritionGroupBy) => void;
}

const GROUP_BY_OPTIONS = [NUTRITION_GROUP_BY_FARM, NUTRITION_GROUP_BY_CROP].map(
  (value) => ({ label: NUTRITION_GROUP_BY_LABELS[value], value })
);

/** Plan mode with Add product switched on (NUTRITION_ADD_REMOVE_PRODUCTS_ENABLED). */
const canAddProducts = (edit: NutritionProgramEdit) =>
  edit.canEdit && NUTRITION_ADD_REMOVE_PRODUCTS_ENABLED;

const HEADER_CELL = 'px-3 pb-2 text-base font-bold text-charcoal';
const VALUE_CELL = 'px-3 py-1.5 text-right tabular-nums';

/**
 * An area-weighted total has no applied/removed split of its own, so its
 * status is judged on the balance alone — pass it through as applied when
 * positive and as removed when negative.
 */
const totalSides = (balance: number) => ({
  applied: Math.max(balance, 0),
  removed: Math.max(-balance, 0),
});

const ColumnHeader: React.FC<{
  label: string;
  unit?: string;
  /** Explains the column on hover — an info icon after the label. */
  help?: string | TipText;
}> = ({ label, unit, help }) => (
  <th className={`${HEADER_CELL} text-right`}>
    <span className="inline-flex items-center gap-1">
      {label}
      {help && (
        <IconTip title={help}>
          <LuInfo size={14} aria-hidden />
        </IconTip>
      )}
    </span>
    {unit && (
      <span className="block text-sm font-normal text-gray-muted">{unit}</span>
    )}
  </th>
);

/**
 * A balance with what makes it up on a muted line under it — "+121.5 −9.2",
 * applied in and removed out, kg/ha — with the words on hover. Shown on
 * expanded rows only, so the collapsed grid stays a clean overview.
 */
const BalanceWithParts: React.FC<{
  nutrient: NutrientKey;
  applied: number;
  removed: number;
  balance: number;
  showParts: boolean;
}> = ({ nutrient, applied, removed, balance, showParts }) => (
  <>
    <NutrientBalanceValue
      nutrient={nutrient}
      applied={applied}
      removed={removed}
      balance={balance}
    />
    {showParts && (applied > 0 || removed > 0) && (
      <Tooltip
        title={
          <TipContent
            tip={NUTRITION_APPLIED_REMOVED_TOOLTIP(
              formatKgPerHa(applied),
              formatKgPerHa(removed)
            )}
          />
        }
      >
        <span className="block cursor-help text-sm text-gray-dark">
          {`+${formatKgPerHa(applied)} −${formatKgPerHa(removed)}`}
        </span>
      </Tooltip>
    )}
  </>
);

const countProducts = (paddock: PaddockNutritionAudit) =>
  paddock.seasons.reduce(
    (sum, season) =>
      sum + season.plans.reduce((n, plan) => n + plan.products.length, 0),
    0
  );

const PaddockNameCell: React.FC<{
  paddock: PaddockNutritionAudit;
  /** Crops under the name — off in the year view, where each column names its crop. */
  showRotation: boolean;
  /** A line under the name instead of the crops — a plan row's year (grouped by crop). */
  subtitle?: string;
}> = ({ paddock, showRotation, subtitle }) => {
  const navigateToFarmsView = useFarmsLinkNavigate();

  return (
    <td className="stack-primary py-1.5 pl-2 pr-4 text-base font-semibold text-charcoal">
      <span className="inline-flex items-center gap-1.5">
        <LinkButton
          className="text-left text-base"
          onClick={() =>
            navigateToFarmsView({
              nodeKey: `${PADDOCK_KEY_PREFIX}${paddock.paddockId}`,
              tab: INPUT_DOC_TAB_PLANNING,
              planningLens: INPUT_DOC_LENS_NUTRITION,
            })
          }
        >
          {paddock.paddockName}
        </LinkButton>
        {paddock.missingRemovalSeasons.length > 0 && (
          <IconTip
            title={NUTRITION_MISSING_REMOVAL_MESSAGE(
              paddock.missingRemovalSeasons
            )}
          >
            <LuAlertTriangle size={14} aria-hidden />
          </IconTip>
        )}
      </span>
      {subtitle ? (
        <span className="block text-sm font-normal text-gray-dark">
          {subtitle}
        </span>
      ) : (
        showRotation && <PaddockRotation paddock={paddock} />
      )}
    </td>
  );
};

// How many years of crops fit under a paddock name before older ones are
// summarised — the full list is in the tooltip.
const ROTATION_YEARS_SHOWN = 3;

/**
 * The crops in range under the paddock name, oldest first, so they're in
 * view with the row collapsed — e.g. "Wheat → Canola → Barley". Two plans in
 * one year read "Wheat + Sorghum". Longer ranges show the latest three, with
 * every year in the tooltip.
 */
const PaddockRotation: React.FC<{ paddock: PaddockNutritionAudit }> = ({
  paddock,
}) => {
  if (paddock.seasons.length === 0) return null;
  const shown = paddock.seasons.slice(-ROTATION_YEARS_SHOWN);
  const isTrimmed = paddock.seasons.length > shown.length;
  const line = `${isTrimmed ? '… → ' : ''}${shown.map(formatYearCrops).join(' → ')}`;
  const tip = {
    title: NUTRITION_ROTATION_TITLE,
    items: paddock.seasons.map((season) =>
      NUTRITION_YEAR_ITEM(season.season, formatYearCrops(season))
    ),
  };
  return (
    <Tooltip title={<TipContent tip={tip} />}>
      <span className="block max-w-[18rem] truncate text-sm font-normal text-gray-dark">
        {line}
      </span>
    </Tooltip>
  );
};

/** Latest planted area, with the year-by-year areas on hover when they change. */
const PlantedArea: React.FC<{ paddock: PaddockNutritionAudit }> = ({
  paddock,
}) => {
  if (paddock.areaHa <= 0) return <span className="text-gray-muted">—</span>;
  if (!paddock.areaVaries) return <>{formatAreaHa(paddock.areaHa)}</>;
  const tip = {
    title: NUTRITION_AREA_VARIES_TITLE,
    items: paddock.seasons.map((season) =>
      NUTRITION_YEAR_ITEM(
        season.season,
        `${formatAreaHa(season.areaHa)} ${NUTRITION_AREA_UNIT_HA}`
      )
    ),
    lines: [NUTRITION_AREA_VARIES_NOTE],
  };
  return (
    <Tooltip title={<TipContent tip={tip} />}>
      <span
        tabIndex={0}
        className="cursor-help underline decoration-gray-medium decoration-dotted underline-offset-4"
      >
        {formatAreaHa(paddock.areaHa)}
      </span>
    </Tooltip>
  );
};

/** Last column — a labelled toggle, so the product detail is easy to find. */
const ProductsToggleCell: React.FC<{
  paddock: PaddockNutritionAudit;
  expanded: boolean;
  onToggle: () => void;
  /** Plan mode: a paddock with plans opens even with no products, to add some. */
  canAdd: boolean;
}> = ({ paddock, expanded, onToggle, canAdd }) => {
  const count = countProducts(paddock);
  if (count === 0 && !(canAdd && paddock.seasons.length > 0)) {
    return (
      <td
        data-label={NUTRITION_COLUMN_PRODUCTS}
        className="px-2 py-1.5 text-right text-gray-muted"
      >
        —
      </td>
    );
  }
  const label = expanded
    ? NUTRITION_COLLAPSE_PRODUCTS_LABEL(paddock.paddockName)
    : NUTRITION_EXPAND_PRODUCTS_LABEL(paddock.paddockName);
  return (
    <td data-label={NUTRITION_COLUMN_PRODUCTS} className="px-2 py-1.5 text-right">
      <button
        type="button"
        className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-base font-semibold text-primary hover:bg-rowHover"
        aria-expanded={expanded}
        aria-label={label}
        onClick={onToggle}
      >
        {NUTRITION_PRODUCT_COUNT_LABEL(count)}
        {expanded ? (
          <LuChevronDown size={14} aria-hidden />
        ) : (
          <LuChevronRight size={14} aria-hidden />
        )}
      </button>
    </td>
  );
};

const BULK_TOGGLE_CLASS = 'inline-flex items-center gap-1 px-2';

const DETAIL_ROW = 'bg-secondary-light';
const DETAIL_LABEL = 'stack-full py-1.5 pr-3';
const DETAIL_VALUE = 'px-3 py-1.5 text-right tabular-nums';

/**
 * A paddock's detail, as rows of the grid itself so every figure sits under
 * its own column heading: each year, then each plan in it (crop · area ·
 * yield), its products with their rates, and the crop's removal. In the
 * balance view figures fall under their nutrient; in the year view, the one
 * nutrient falls under the product's year.
 */
const PaddockDetailRows: React.FC<{
  paddock: PaddockNutritionAudit;
  isSeasons: boolean;
  /** Balance view columns. */
  nutrients: NutrientKey[];
  /** Year view columns, oldest first. */
  years: number[];
  trendNutrient: NutrientKey;
  edit: NutritionProgramEdit;
  yieldLabel: string;
  /** One plan grouped by crop: no year heading (the row shows it) and no crop on the plan line. */
  singlePlan?: boolean;
}> = ({
  paddock,
  isSeasons,
  nutrients,
  years,
  trendNutrient,
  edit,
  yieldLabel,
  singlePlan = false,
}) => {
  // One cell per value column, then the empty Products column.
  const valueCells = (
    year: number,
    valueOf: (key: NutrientKey) => number,
    format: (value: number) => string,
    className = 'text-charcoal'
  ) => (
    <>
      {isSeasons ? (
        <>
          {years.map((y) => {
            const value = y === year ? valueOf(trendNutrient) : 0;
            return (
              <td
                key={y}
                data-label={String(y)}
                className={`${DETAIL_VALUE} ${className}`}
              >
                {value > 0 ? format(value) : ''}
              </td>
            );
          })}
          <td className="stack-empty" />
        </>
      ) : (
        nutrients.map((key) => {
          const value = valueOf(key);
          return (
            <td
              key={key}
              data-label={NUTRIENT_SHORT_LABELS[key]}
              className={`${DETAIL_VALUE} ${className}`}
            >
              {value > 0 ? (
                format(value)
              ) : (
                <span className="text-gray-muted">—</span>
              )}
            </td>
          );
        })
      )}
      <td className="stack-empty" />
    </>
  );
  // Year view: each year plus Total; balance view: each nutrient.
  const valueColumnKeys: string[] = isSeasons
    ? [...years.map(String), 'total']
    : nutrients;
  const emptyCells = (
    <>
      {valueColumnKeys.map((key) => (
        <td key={key} className="stack-empty" />
      ))}
      <td className="stack-empty" />
    </>
  );

  // Plan mode lists every year in range, so a year with no plan says how to
  // get one; otherwise only years with a plan show.
  const rows = canAddProducts(edit) && !singlePlan
    ? years.map(
        (year) =>
          paddock.seasons.find((s) => s.season === year) ?? { missing: year }
      )
    : paddock.seasons;

  return (
    <>
      {rows.map((season) =>
        'missing' in season ? (
          <tr
            key={season.missing}
            className={`${DETAIL_ROW} border-t border-gray-bright`}
          >
            <td
              colSpan={2}
              className={`${DETAIL_LABEL} pl-6 pt-3 text-charcoal`}
            >
              <span className="font-bold">{season.missing}</span>
              <span className="ml-3 text-gray-muted">
                {NUTRITION_SEASON_NO_PLAN_MESSAGE}
              </span>
            </td>
            {emptyCells}
          </tr>
        ) : (
        <React.Fragment key={season.season}>
          {!singlePlan && (
            <tr className={`${DETAIL_ROW} border-t border-gray-bright`}>
              <td
                colSpan={2}
                className={`${DETAIL_LABEL} pl-6 pt-3 font-bold text-charcoal`}
              >
                {season.season}
              </td>
              {emptyCells}
            </tr>
          )}
          {season.plans.map((plan) => {
            const hasRemoval = NUTRIENT_DISPLAY_ORDER.some(
              (key) => plan.removed[key] > 0
            );
            return (
              <React.Fragment key={plan.inputId}>
                <tr className={DETAIL_ROW}>
                  <td
                    colSpan={2}
                    className={`${DETAIL_LABEL} pl-8 text-sm font-semibold text-gray-dark`}
                  >
                    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="inline-flex items-center gap-1.5">
                        {formatPlanSummary(plan, yieldLabel, !singlePlan)}
                        {plan.removalMissing && (
                          <IconTip
                            title={NUTRITION_MISSING_REMOVAL_MESSAGE([
                              season.season,
                            ])}
                          >
                            <LuAlertTriangle size={14} aria-hidden />
                          </IconTip>
                        )}
                      </span>
                      {canAddProducts(edit) && (
                        <AddProductControl
                          plan={plan}
                          location={NUTRITION_PADDOCK_YEAR_LABEL(
                            paddock.paddockName,
                            season.season
                          )}
                          edit={edit}
                        />
                      )}
                    </span>
                  </td>
                  {emptyCells}
                </tr>
                {plan.products.length === 0 && (
                  <tr className={DETAIL_ROW}>
                    <td
                      colSpan={2}
                      className={`${DETAIL_LABEL} pl-10 text-gray-muted`}
                    >
                      {NUTRITION_SEASON_NO_PRODUCTS_LABEL}
                    </td>
                    {emptyCells}
                  </tr>
                )}
                {plan.products.map((product) => (
                  <ProductRowMenu key={product.key} line={product} edit={edit}>
                    <tr className={`${DETAIL_ROW} hover:bg-rowHover`}>
                      <td colSpan={2} className={`${DETAIL_LABEL} pl-10`}>
                        {/* Name left, rate right — every rate in one column. */}
                        <span className="flex items-center justify-between gap-4 text-charcoal">
                          <ProductName line={product} />
                          <span className="inline-flex items-center gap-4">
                            <span className="text-sm text-gray-dark">
                              <ProductDate line={product} />
                            </span>
                            <ProductRate line={product} edit={edit} />
                          </span>
                        </span>
                      </td>
                      {valueCells(
                        season.season,
                        (key) => product.perHa[key] ?? 0,
                        formatKgPerHa
                      )}
                    </tr>
                  </ProductRowMenu>
                ))}
                {hasRemoval && (
                  // The plan's crop removal — a ruled, bold line under its
                  // products, figures as minus, so it reads as what went out.
                  <tr className={`${DETAIL_ROW} border-t border-gray-medium`}>
                    <td
                      colSpan={2}
                      className={`${DETAIL_LABEL} pl-10 font-semibold text-charcoal`}
                    >
                      {NUTRITION_ROW_CROP_REMOVAL}
                    </td>
                    {valueCells(
                      season.season,
                      (key) => plan.removed[key],
                      (value) => `−${formatKgPerHa(value)}`,
                      'font-semibold text-charcoal'
                    )}
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </React.Fragment>
        )
      )}
    </>
  );
};

const TotalRow: React.FC<{
  total: AreaWeightedBalance;
  view: NutritionFarmView;
  nutrients: NutrientKey[];
  trendNutrient: NutrientKey;
  years: number[];
  label: string;
}> = ({ total, view, nutrients, trendNutrient, years, label }) => (
  <tr className="border-b border-gray-bright bg-secondary-light">
    <td className="stack-primary py-1.5 pl-2 pr-4 text-base font-semibold text-gray-dark">
      <span className="inline-flex items-center gap-1">
        {label}
        <IconTip title={NUTRITION_FARM_TOTAL_HELP}>
          <LuInfo size={14} aria-hidden />
        </IconTip>
      </span>
    </td>
    <td
      data-label={NUTRITION_COLUMN_AREA}
      className={`${VALUE_CELL} text-gray-dark`}
    >
      {formatAreaHa(total.areaHa)}
    </td>
    {view === NUTRITION_FARM_VIEW_SEASONS ? (
      <>
        {years.map((year) => (
          <td key={year} className="stack-empty" />
        ))}
        <td data-label={NUTRITION_COLUMN_TOTAL} className={VALUE_CELL}>
          <NutrientBalanceValue
            nutrient={trendNutrient}
            {...totalSides(total.balance[trendNutrient])}
            balance={total.balance[trendNutrient]}
          />
        </td>
      </>
    ) : (
      nutrients.map((key) => (
        <td
          key={key}
          data-label={NUTRIENT_SHORT_LABELS[key]}
          className={VALUE_CELL}
        >
          <NutrientBalanceValue
            nutrient={key}
            {...totalSides(total.balance[key])}
            balance={total.balance[key]}
          />
        </td>
      ))
    )}
    <td className="stack-empty" />
  </tr>
);

/**
 * A plan row (grouped by crop) as a one-year, one-plan paddock, so it uses
 * the same cells and detail rows as a paddock — its figures are the plan's.
 */
const planAsPaddock = (row: CropPlanRow): PaddockNutritionAudit => {
  const { plan } = row;
  return {
    paddockId: row.paddockId,
    paddockName: row.paddockName,
    farmId: '',
    farmName: row.farmName,
    areaHa: plan.areaHa,
    averageAreaHa: plan.areaHa,
    areaVaries: false,
    seasons: [
      {
        season: row.season,
        areaHa: plan.areaHa,
        plans: [plan],
        cropNames: [plan.cropName],
        yields: plan.yields,
        applied: plan.applied,
        removed: plan.removed,
        balance: row.balance,
        removalMissing: plan.removalMissing,
      },
    ],
    applied: plan.applied,
    removed: plan.removed,
    balance: row.balance,
    missingRemovalSeasons: plan.removalMissing ? [row.season] : [],
  };
};

/**
 * Farm / org nutrient balance — one row per paddock, grouped by farm, each
 * cell the paddock's balance (kg/ha) over the range with its status. The
 * seasons view shows one nutrient, season by season, with the crop under
 * each figure. Each farm ends with an area-weighted total row, and an org
 * with more than one farm ends with one for the whole scope.
 *
 * Grouped by crop (OP-2523), each row is one plan instead, under its crop,
 * across every year in range — labelled by its paddock and year, since the
 * group names the crop — with a total per crop.
 */
const FarmNutritionTable: React.FC<FarmNutritionTableProps> = ({
  audit,
  view,
  nutrients,
  trendNutrient,
  edit,
  yieldLabel,
  showCropDetails,
  groupBy,
  onGroupByChange,
}) => {
  const byCrop = groupBy === NUTRITION_GROUP_BY_CROP;
  // Plan rows, as one-plan paddocks so the paddock cells render them.
  const { cropGroups, cropTotal } = useMemo(() => {
    if (!byCrop) return { cropGroups: [], cropTotal: null };
    const grouping = groupPlansByCrop(audit);
    return {
      cropGroups: grouping.groups.map((group) => ({
        ...group,
        rows: group.rows.map((row) => ({ row, paddock: planAsPaddock(row) })),
      })),
      cropTotal: grouping.total,
    };
  }, [audit, byCrop]);
  // Rows start collapsed — the grid is the overview, with the crops under
  // each paddock name; products open per paddock or all at once.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const isExpanded = (paddockId: string) => expanded.has(paddockId);
  // Grouped by crop, each crop's plans fold away to its total row; crops
  // start open.
  const [collapsedCrops, setCollapsedCrops] = useState<Set<string>>(
    () => new Set()
  );
  const toggleCrop = (cropName: string) =>
    setCollapsedCrops((prev) => {
      const next = new Set(prev);
      if (next.has(cropName)) next.delete(cropName);
      else next.add(cropName);
      return next;
    });
  const isSeasons = view === NUTRITION_FARM_VIEW_SEASONS;
  const valueColumnCount = isSeasons
    ? audit.seasons.length + 1
    : nutrients.length;
  // Paddock, area, the value columns, then products.
  const colSpan = 3 + valueColumnCount;

  // Paddocks with products have detail to show; in Plan mode, so does any
  // paddock with a plan, so products can be added to it.
  const hasDetail = (paddock: PaddockNutritionAudit) =>
    countProducts(paddock) > 0 ||
    (canAddProducts(edit) && paddock.seasons.length > 0);
  const expandableIds = byCrop
    ? cropGroups.flatMap((group) =>
        group.rows
          .filter(({ paddock }) => hasDetail(paddock))
          .map(({ row }) => row.key)
      )
    : audit.farms.flatMap((farm) =>
        farm.paddocks.filter(hasDetail).map((paddock) => paddock.paddockId)
      );

  const toggle = (paddockId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(paddockId)) next.delete(paddockId);
      else next.add(paddockId);
      return next;
    });

  const renderValueCells = (
    paddock: PaddockNutritionAudit,
    expandKey: string = paddock.paddockId
  ) => {
    if (paddock.seasons.length === 0) {
      return (
        <td
          colSpan={valueColumnCount}
          className="stack-full px-3 py-1.5 text-base text-gray-muted"
        >
          {NUTRITION_NO_PLANS_MESSAGE}
        </td>
      );
    }

    if (isSeasons) {
      return (
        <>
          {audit.seasons.map((year) => {
            const season = paddock.seasons.find((s) => s.season === year);
            return (
              <td key={year} data-label={String(year)} className={VALUE_CELL}>
                {season ? (
                  <>
                    <BalanceWithParts
                      nutrient={trendNutrient}
                      applied={season.applied[trendNutrient]}
                      removed={season.removed[trendNutrient]}
                      balance={season.balance[trendNutrient]}
                      showParts={isExpanded(expandKey)}
                    />
                    {/* Grouped by crop, the crop is the group heading. */}
                    {!byCrop && (
                      <span className="block text-sm text-gray-muted">
                        {formatSeasonCrops(season, yieldLabel, showCropDetails)}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-gray-muted">—</span>
                )}
              </td>
            );
          })}
          <td data-label={NUTRITION_COLUMN_TOTAL} className={VALUE_CELL}>
            <BalanceWithParts
              nutrient={trendNutrient}
              applied={paddock.applied[trendNutrient]}
              removed={paddock.removed[trendNutrient]}
              balance={paddock.balance[trendNutrient]}
              showParts={isExpanded(expandKey)}
            />
          </td>
        </>
      );
    }

    return nutrients.map((key) => (
      <td
        key={key}
        data-label={NUTRIENT_SHORT_LABELS[key]}
        className={VALUE_CELL}
      >
        <BalanceWithParts
          nutrient={key}
          applied={paddock.applied[key]}
          removed={paddock.removed[key]}
          balance={paddock.balance[key]}
          showParts={isExpanded(expandKey)}
        />
      </td>
    ));
  };

  return (
    <div className="plan-preview-container overflow-x-auto">
      {/* How the rows are grouped, then opening and closing them — together,
          as they act on the same rows. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-dark">
          {NUTRITION_GROUP_BY_LABEL}
        </span>
        <Segmented<NutritionGroupBy>
          aria-label={NUTRITION_GROUP_BY_LABEL}
          value={groupBy}
          options={GROUP_BY_OPTIONS}
          onChange={onGroupByChange}
        />
      {(expandableIds.length > 0 || (byCrop && cropGroups.length > 0)) && (
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className={BULK_TOGGLE_CLASS}
            onClick={() => {
              setCollapsedCrops(new Set());
              setExpanded(new Set(expandableIds));
            }}
          >
            <LuChevronsUpDown size={14} aria-hidden />
            {NUTRITION_EXPAND_ALL_LABEL}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className={BULK_TOGGLE_CLASS}
            onClick={() => {
              // Grouped by crop, Collapse all folds every crop to its total.
              if (byCrop) {
                setCollapsedCrops(new Set(cropGroups.map((g) => g.cropName)));
              }
              setExpanded(new Set());
            }}
          >
            <LuChevronsDownUp size={14} aria-hidden />
            {NUTRITION_COLLAPSE_ALL_LABEL}
          </Button>
        </>
      )}
      </div>
      <table
        className={`${isSeasons ? 'stack-table-narrow' : 'stack-table-wide'} w-max border-collapse text-base`}
      >
        <thead>
          <tr className="border-b border-gray-bright">
            <th className={`${HEADER_CELL} pl-2 text-left`}>
              {NUTRITION_COLUMN_PADDOCK}
            </th>
            <ColumnHeader
              label={NUTRITION_COLUMN_AREA}
              help={NUTRITION_AREA_COLUMN_HELP}
            />
            {isSeasons ? (
              <>
                {audit.seasons.map((year) => (
                  <ColumnHeader
                    key={year}
                    label={String(year)}
                    unit={`${NUTRIENT_SHORT_LABELS[trendNutrient]} ${NUTRITION_UNIT_KG_PER_HA}`}
                                      />
                ))}
                <ColumnHeader
                  label={NUTRITION_COLUMN_TOTAL}
                  unit={`${NUTRIENT_SHORT_LABELS[trendNutrient]} ${NUTRITION_UNIT_KG_PER_HA}`}
                                  />
              </>
            ) : (
              nutrients.map((key) => (
                <ColumnHeader
                  key={key}
                  label={NUTRIENT_SHORT_LABELS[key]}
                  unit={NUTRITION_UNIT_KG_PER_HA}
                                  />
              ))
            )}
            <ColumnHeader label={NUTRITION_COLUMN_PRODUCTS} />
          </tr>
        </thead>
        <tbody>
          {byCrop &&
            cropGroups.map((group) => (
              <React.Fragment key={group.cropName}>
                <tr>
                  <td
                    colSpan={colSpan}
                    className="stack-full px-2 pb-1 pt-4 text-left"
                  >
                    <button
                      type="button"
                      className="inline-flex cursor-pointer items-center gap-1.5 rounded px-1 py-0.5 text-base font-bold text-gray-dark hover:bg-rowHover"
                      aria-expanded={!collapsedCrops.has(group.cropName)}
                      aria-label={
                        collapsedCrops.has(group.cropName)
                          ? NUTRITION_EXPAND_CROP_LABEL(group.cropName)
                          : NUTRITION_COLLAPSE_CROP_LABEL(group.cropName)
                      }
                      onClick={() => toggleCrop(group.cropName)}
                    >
                      {collapsedCrops.has(group.cropName) ? (
                        <LuChevronRight size={16} aria-hidden />
                      ) : (
                        <LuChevronDown size={16} aria-hidden />
                      )}
                      {group.cropName}
                      <span className="text-sm font-normal text-gray-muted">
                        {NUTRITION_PLAN_COUNT_LABEL(group.rows.length)}
                      </span>
                    </button>
                  </td>
                </tr>
                {!collapsedCrops.has(group.cropName) &&
                  group.rows.map(({ row, paddock }) => (
                  <React.Fragment key={row.key}>
                    <tr className="border-b border-gray-bright hover:bg-rowHover">
                      <PaddockNameCell
                        paddock={paddock}
                        showRotation={false}
                        subtitle={
                          audit.farms.length > 1
                            ? NUTRITION_PLAN_YEAR_FARM_LABEL(
                                row.season,
                                row.farmName
                              )
                            : String(row.season)
                        }
                      />
                      <td
                        data-label={NUTRITION_COLUMN_AREA}
                        className={`${VALUE_CELL} text-charcoal`}
                      >
                        <PlantedArea paddock={paddock} />
                      </td>
                      {renderValueCells(paddock, row.key)}
                      <ProductsToggleCell
                        paddock={paddock}
                        expanded={isExpanded(row.key)}
                        onToggle={() => toggle(row.key)}
                        canAdd={canAddProducts(edit)}
                      />
                    </tr>
                    {isExpanded(row.key) && hasDetail(paddock) && (
                      <PaddockDetailRows
                        paddock={paddock}
                        isSeasons={isSeasons}
                        nutrients={nutrients}
                        years={audit.seasons}
                        trendNutrient={trendNutrient}
                        edit={edit}
                        yieldLabel={yieldLabel}
                        singlePlan
                      />
                    )}
                  </React.Fragment>
                ))}
                <TotalRow
                  total={group}
                  view={view}
                  nutrients={nutrients}
                  trendNutrient={trendNutrient}
                  years={audit.seasons}
                  label={NUTRITION_ROW_FARM_TOTAL_FOR(group.cropName)}
                />
              </React.Fragment>
            ))}
          {byCrop && cropTotal && cropGroups.length > 1 && (
            <TotalRow
              total={cropTotal}
              view={view}
              nutrients={nutrients}
              trendNutrient={trendNutrient}
              years={audit.seasons}
              label={NUTRITION_ROW_FARM_TOTAL}
            />
          )}
          {!byCrop &&
            audit.farms.map((farm) => (
            <React.Fragment key={farm.farmId}>
              {audit.farms.length > 1 && (
                <tr>
                  <td
                    colSpan={colSpan}
                    className="stack-full px-2 pb-1 pt-4 text-left text-base font-bold text-gray-dark"
                  >
                    {farm.farmName}
                  </td>
                </tr>
              )}
              {farm.paddocks.map((paddock) => (
                <React.Fragment key={paddock.paddockId}>
                  <tr className="border-b border-gray-bright hover:bg-rowHover">
                    <PaddockNameCell
                      paddock={paddock}
                      showRotation={!isSeasons}
                    />
                    <td
                      data-label={NUTRITION_COLUMN_AREA}
                      className={`${VALUE_CELL} text-charcoal`}
                    >
                      <PlantedArea paddock={paddock} />
                    </td>
                    {renderValueCells(paddock)}
                    <ProductsToggleCell
                      paddock={paddock}
                      expanded={isExpanded(paddock.paddockId)}
                      onToggle={() => toggle(paddock.paddockId)}
                      canAdd={canAddProducts(edit)}
                    />
                  </tr>
                  {isExpanded(paddock.paddockId) &&
                    hasDetail(paddock) && (
                      <PaddockDetailRows
                        paddock={paddock}
                        isSeasons={isSeasons}
                        nutrients={nutrients}
                        years={audit.seasons}
                        trendNutrient={trendNutrient}
                        edit={edit}
                        yieldLabel={yieldLabel}
                      />
                    )}
                </React.Fragment>
              ))}
              <TotalRow
                total={farm}
                view={view}
                nutrients={nutrients}
                trendNutrient={trendNutrient}
                years={audit.seasons}
                label={
                  audit.farms.length > 1
                    ? NUTRITION_ROW_FARM_TOTAL_FOR(farm.farmName)
                    : NUTRITION_ROW_FARM_TOTAL
                }
              />
            </React.Fragment>
          ))}
          {!byCrop && audit.farms.length > 1 && (
            <TotalRow
              total={audit.total}
              view={view}
              nutrients={nutrients}
              trendNutrient={trendNutrient}
              years={audit.seasons}
              label={NUTRITION_ROW_FARM_TOTAL}
            />
          )}
        </tbody>
      </table>
    </div>
  );
};

export default FarmNutritionTable;
